import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FAQContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    property_interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const faqs = [
    {
      question: "How does the blockchain verification work?",
      answer: "Every property listed on MyDoor360 has its title and ownership history recorded on our decentralized blockchain. This creates an immutable record that can be instantly verified, ensuring you're dealing with legitimate properties and owners.",
    },
    {
      question: "What are the credit and background check requirements?",
      answer: "To ensure the safety of property owners and maintain platform integrity, all users must pass a standard credit check and background verification before gaining access to self-guided property tours. These checks are processed securely and typically complete within 24-48 hours.",
    },
    {
      question: "How do self-guided property tours work?",
      answer: "Once verified, you'll receive digital access codes for properties you're interested in. These one-time codes allow you to tour properties at your convenience within scheduled windows. All access is logged on the blockchain for security.",
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, all subscription plans are month-to-month with no long-term commitment. You can cancel at any time from your dashboard, and you'll retain access until the end of your billing period.",
    },
    {
      question: "How are smart contracts used in transactions?",
      answer: "Our smart contracts automate key parts of real estate transactions. They hold funds in escrow, verify conditions are met (inspections, financing, etc.), and automatically execute transfers when all parties fulfill their obligations—reducing delays and eliminating intermediary fees.",
    },
    {
      question: "Is my personal information secure?",
      answer: "Absolutely. We use bank-level encryption for all personal and financial data. Credit checks are processed through certified partners, and we never store sensitive information beyond what's necessary. Your blockchain identity is pseudonymous, adding an extra layer of privacy.",
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        property_interest: "",
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="faq" className="py-24 lg:py-32 bg-white" data-testid="faq-contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* FAQ Section */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C04000] mb-4 block">
              FAQ
            </span>
            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="faq-heading"
            >
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 mb-8">
              Find answers to common questions about our blockchain-powered real estate platform.
            </p>

            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-slate-200">
                  <AccordionTrigger 
                    className="text-left font-semibold text-slate-900 hover:text-[#C04000] py-4"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                    data-testid={`faq-trigger-${index}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Contact Form */}
          <div id="contact">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C04000] mb-4 block">
              Contact Us
            </span>
            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="contact-heading"
            >
              Get in Touch
            </h2>
            <p className="text-slate-600 mb-8">
              Have questions? Our team is here to help you navigate your blockchain real estate journey.
            </p>

            {submitted ? (
              <div className="bg-[#FFF3ED] rounded-lg p-8 text-center" data-testid="contact-success">
                <CheckCircle className="w-12 h-12 text-[#C04000] mx-auto mb-4" />
                <h3 
                  className="text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Message Sent!
                </h3>
                <p className="text-slate-600 mb-4">
                  Thanks for reaching out. We'll get back to you within 24 hours.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSubmitted(false)}
                  className="border-slate-300"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your name"
                      className="border-slate-300 focus:border-[#C04000] focus:ring-[#C04000]/20"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="you@example.com"
                      className="border-slate-300 focus:border-[#C04000] focus:ring-[#C04000]/20"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="border-slate-300 focus:border-[#C04000] focus:ring-[#C04000]/20"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="property_interest" className="text-slate-700">Property Interest</Label>
                    <Input
                      id="property_interest"
                      name="property_interest"
                      value={formData.property_interest}
                      onChange={handleInputChange}
                      placeholder="e.g., Buying, Renting, Investing"
                      className="border-slate-300 focus:border-[#C04000] focus:ring-[#C04000]/20"
                      data-testid="contact-interest-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="How can we help you?"
                    rows={5}
                    className="border-slate-300 focus:border-[#C04000] focus:ring-[#C04000]/20"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C04000] hover:bg-[#A03500] text-white"
                  data-testid="contact-submit-button"
                >
                  {submitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQContactSection;
